import React, { useEffect, useRef, useState } from 'react';
import { BTN_PRIMARY, EvTag, SevPill } from './parts';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COLUMN_LABEL = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-dim';

function Issue({ issue }) {
  return (
    <article className="grid gap-2.5 rounded-xl border border-white/10 bg-deep px-[18px] py-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <SevPill severity={issue.severity} />
        <h4 className="m-0 flex-[1_1_220px] text-base font-semibold">{issue.title}</h4>
        <EvTag ev={issue.ev || 'observed'} />
      </div>
      <div className="grid grid-cols-1 gap-3 text-[13.5px] text-mist sm:grid-cols-2">
        <div className="min-w-0 [overflow-wrap:anywhere]"><b className={COLUMN_LABEL}>What we saw</b>{issue.evidence || issue.detail}</div>
        <div className="min-w-0 [overflow-wrap:anywhere]"><b className={COLUMN_LABEL}>Fix</b>{issue.fix}</div>
      </div>
    </article>
  );
}

function Gate({ count, onUnlock, unlocking, unlockError }) {
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');
  const message = localError || unlockError;

  const submit = (event) => {
    event.preventDefault();
    if (unlocking) return;
    const value = email.trim();
    if (!EMAIL_RE.test(value)) { setLocalError('Enter an email like you@business.com.'); return; }
    setLocalError('');
    if (onUnlock) onUnlock({ email: value });
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className="w-full max-w-[460px] rounded-[18px] border border-white/20 bg-raised p-5 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)]"
    >
      <h4 className="m-0 mb-1 font-display text-[19px] font-semibold">{count} more finding{count === 1 ? '' : 's'}</h4>
      <p className="m-0 mb-3.5 text-[13.5px] text-mist">
        Get the full report with every fix, the keyword plan and the compliance notes. We email it to you, and we may follow up once about the fixes.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <label
          htmlFor="site-check-email"
          className="flex min-h-[46px] min-w-0 flex-1 items-center rounded-xl border border-white/20 bg-ink px-3.5 transition-[border-color,box-shadow] focus-within:border-sky focus-within:shadow-[0_0_0_3px_rgba(94,160,255,0.18)]"
        >
          <span className="sr-only">Your email</span>
          <input
            id="site-check-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@business.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (localError) setLocalError(''); }}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? 'site-check-email-error' : undefined}
            className="w-full min-w-0 border-0 bg-transparent p-0 text-base text-cream outline-none placeholder:text-dim"
          />
        </label>
        <button type="submit" disabled={unlocking} className={`${BTN_PRIMARY} min-h-[46px] !rounded-xl`}>
          {unlocking ? 'Sending…' : 'Send me the report'}
        </button>
      </div>
      {message ? <p id="site-check-email-error" role="alert" className="mb-0 mt-2.5 text-xs text-[#FF6B6B]">{message}</p> : null}
    </form>
  );
}

// Top issues in full, then the locked rest blurred behind the email gate.
// Once unlocked the API returns every issue in full and the gate goes away.
export default function IssueList({ issues = [], lockedCount = 0, unlocked = false, emailed = false, onUnlock, unlocking = false, unlockError = '' }) {
  const full = issues.filter((issue) => !issue.locked);
  const locked = issues.filter((issue) => issue.locked);
  const shut = locked.length > 0 && !unlocked;
  const startedLocked = useRef(!unlocked);
  const noteRef = useRef(null);
  const justUnlocked = unlocked && startedLocked.current;

  useEffect(() => {
    if (justUnlocked && noteRef.current) noteRef.current.focus();
  }, [justUnlocked]);

  if (!issues.length) {
    return <p className="m-0 rounded-xl border border-white/10 bg-deep px-[18px] py-4 text-sm text-mist">No issues on the pages we read.</p>;
  }

  return (
    <div>
      <div className="grid gap-2.5">
        {full.map((issue, index) => <Issue key={issue.code || `${index}-${issue.title}`} issue={issue} />)}
      </div>
      {shut ? (
        <div className="relative mt-2.5 min-h-[23rem] overflow-hidden rounded-xl border border-white/10 sm:min-h-[18rem]">
          <ul className="m-0 grid list-none select-none px-[18px] py-1.5 blur-[5px]" aria-hidden="true">
            {locked.map((issue, index) => (
              <li key={`${index}-${issue.title}`} className="flex items-center gap-2.5 border-b border-white/10 py-[11px] text-[14.5px] last:border-b-0">
                <SevPill severity={issue.severity} />
                <span className="flex-1">{issue.title}</span>
                <EvTag ev="observed" />
              </li>
            ))}
          </ul>
          <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-ink/35 to-ink/85 p-4">
            <Gate count={lockedCount || locked.length} onUnlock={onUnlock} unlocking={unlocking} unlockError={unlockError} />
          </div>
        </div>
      ) : null}
      {justUnlocked ? (
        <p ref={noteRef} tabIndex={-1} role="status" className="mb-0 mt-2.5 text-[13px] text-[#3DD68C] focus:outline-none">
          {emailed ? 'Unlocked. We sent the report to your inbox.' : 'Unlocked. Every finding is open below.'}
        </p>
      ) : null}
    </div>
  );
}
